import { useCallback, useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

export const useScrollAnimation = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const sectionsRef = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const useSectionInView = (sectionId: string) => {
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, {
      amount: 0.5,
      margin: "-20% 0px -20% 0px",
    });

    useEffect(() => {
      if (isInView) {
        setActiveSection(sectionId);
      }
    }, [isInView, sectionId]);

    useEffect(() => {
      sectionsRef.current[sectionId] = ref.current;
    }, [sectionId]);

    return { ref, isInView };
  };

  const scrollToSection = useCallback((sectionId: string) => {
    const section = sectionsRef.current[sectionId];
    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, []);

  const scrollToNextSection = useCallback(
    (currentSectionId: string) => {
      const sectionIds = Object.keys(sectionsRef.current);
      const currentIndex = sectionIds.indexOf(currentSectionId);
      const nextIndex = (currentIndex + 1) % sectionIds.length;
      const nextSectionId = sectionIds[nextIndex];

      if (nextSectionId) {
        scrollToSection(nextSectionId);
      }
    },
    [scrollToSection]
  );

  return {
    activeSection,
    useSectionInView,
    scrollToSection,
    scrollToNextSection,
  };
};
